// Recidiviz - a data platform for criminal justice reform
// Copyright (C) 2024 Recidiviz, Inc.
//
// This program is free software: you can redistribute it and/or modify
// it under the terms of the GNU General Public License as published by
// the Free Software Foundation, either version 3 of the License, or
// (at your option) any later version.
//
// This program is distributed in the hope that it will be useful,
// but WITHOUT ANY WARRANTY; without even the implied warranty of
// MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
// GNU General Public License for more details.
//
// You should have received a copy of the GNU General Public License
// along with this program.  If not, see <https://www.gnu.org/licenses/>.
// =============================================================================

import { Sans14, spacing } from "@recidiviz/design-system";
import { rem } from "polished";
import { Link } from "react-router-dom";
import styled from "styled-components";

import { WorkflowsResidentMetadata } from "~datatypes";
import { Icon } from "~design-system";
import { palette } from "~design-system";

import { DateInfo, DatesTable } from "../../DatesTable";
import { DetailsHeading, DetailsSection } from "../../styles";
import { ResidentProfileProps } from "../../types";

const DateCalculationInfo = styled(Sans14)`
  color: ${palette.slate70};
  &:hover {
    color: ${palette.slate};
  }
`;

const DateMethodologyText = styled(Sans14)`
  display: inline;

  border-bottom: 1px dashed ${palette.pine3};
  padding-bottom: ${rem(spacing.xxs)};
`;

export function metadataToDates(
  metadata: WorkflowsResidentMetadata<"US_AZ">,
  useDtp: boolean,
  inTableTooltip: string,
): DateInfo[] {
  const hasAcisDates = useDtp ? !!metadata.acisDtpDate : !!metadata.acisTprDate;

  // Show ACIS date if we have it
  const acisDate = useDtp
    ? [
        {
          label: "DTP",
          date: metadata.acisDtpDate,
        },
      ]
    : [
        {
          label: "TPR",
          date: metadata.acisTprDate,
        },
      ];

  const realDates = [...acisDate];

  // If we don't have a date from ACIS, show the projected TPR/DTP date
  const projectedDates = useDtp
    ? [
        {
          label: "Projected DTP",
          date: metadata.projectedDtpDate,
          tooltip: inTableTooltip,
          highlight: true,
        },
      ]
    : [
        {
          label: "Projected TPR",
          date: metadata.projectedTprDate,
          tooltip: inTableTooltip,
          highlight: true,
        },
      ];

  const csbdMapping: Record<string, string> = {
    "COMMUNITY SUPERVISION BEGIN DATE": "CSBD",
    "TRANSITION TO ABSOLUTE DISCHARGE DATE": "TR to ADD",
  };
  const ercdMapping: Record<string, string> = {
    "EARNED RELEASE CREDIT DATE": "ERCD",
    "ABSOLUTE DISCHARGE DATE": "ADD",
  };

  const csbdDisplayName =
    metadata.csbdOrTrToAdd && metadata.csbdOrTrToAdd.trim() !== ""
      ? csbdMapping[metadata.csbdOrTrToAdd]
      : "CSBD / TR to ADD";
  const ercdDisplayName =
    metadata.ercdOrAdd && metadata.ercdOrAdd.trim() !== ""
      ? ercdMapping[metadata.ercdOrAdd]
      : "ERCD";

  // Always show CSBD, ERCD, SED, CSED dates
  return [
    ...(hasAcisDates ? realDates : projectedDates),
    { label: csbdDisplayName, date: metadata.csbdDate },
    { label: ercdDisplayName, date: metadata.ercdDate },
    { label: "SED", date: metadata.sedDate },
    { label: "CSED", date: metadata.csedDate },
  ];
}

export function UsAzDates({
  resident,
}: ResidentProfileProps): React.ReactElement<any> | null {
  const { metadata } = resident;

  if (metadata.stateCode !== "US_AZ") return null;

  // Only show a DTP date for people with a DTP opportunity; by default show TPR
  const useDtp = Boolean(
    resident.flattenedOpportunities.find(
      (opp) => opp.type === "usAzReleaseToDTP",
    ),
  );

  const inTableTooltip =
    "In cases where Time Comp has not yet assigned a date for STP or DTP release, Recidiviz uses ADCRR policy to project the release date. We include this projected date here to help CO IIIs prioritize home plans and other release planning. Time Comp will make the final determination on release date once all transition release criteria have been met. As such, this date should not be shared with inmates.  For more details on how Recidiviz projects release dates, please click on “How are these dates calculated?” below.";

  const dates = metadataToDates(metadata, useDtp, inTableTooltip);
  const hasAcisDates = useDtp ? !!metadata.acisDtpDate : !!metadata.acisTprDate;

  return (
    <DetailsSection>
      <DetailsHeading>Dates to Keep Track of</DetailsHeading>

      <DatesTable dates={dates} highlightPastDates />

      {!hasAcisDates && (
        <Link
          to={
            "https://drive.google.com/file/d/13sj_5uRGKNEw1J9O-E3h-ohivKyv2k2k/view"
          }
          target="_blank"
        >
          <DateCalculationInfo>
            <DateMethodologyText>
              {"How are these dates calculated? "}
            </DateMethodologyText>
            <Icon kind="Info" size={12} />
          </DateCalculationInfo>
        </Link>
      )}
    </DetailsSection>
  );
}
